import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { LoginCredentials } from '../types'
import type { Database } from '@/shared/types/supabase'

type UserRole = Database['public']['Enums']['user_role']

/**
 * Extranet login - for OWNER, COUNCIL_MEMBER roles only
 */
export function useExtranetLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      // Step 1: Sign in with email/password
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword(
        credentials
      )

      if (authError) throw authError
      if (!authData.user) throw new Error('No user returned after login')

      // Step 2: Check if user has appropriate role for extranet
      const { data: userResidences, error: residencesError } = await supabase
        .from('user_residences')
        .select('role')
        .eq('user_id', authData.user.id)

      if (residencesError) {
        // Sign out the user since they can't access extranet
        await supabase.auth.signOut()
        throw residencesError
      }

      // Step 3: Verify user has at least one extranet role
      const extranetRoles: UserRole[] = ['OWNER', 'COUNCIL_MEMBER']
      const hasExtranetAccess = userResidences?.some((ur) =>
        extranetRoles.includes(ur.role)
      )

      if (!hasExtranetAccess) {
        // Sign out the user - they don't have extranet access
        await supabase.auth.signOut()
        throw new Error('EXTRANET_ACCESS_DENIED')
      }

      return authData
    },
    onSuccess: async () => {
      // Invalidate and refetch auth and agency queries immediately
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth'] }),
        queryClient.invalidateQueries({ queryKey: ['agencies'] }),
      ])
      // Force refetch to ensure data is loaded before navigation
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['auth'] }),
        queryClient.refetchQueries({ queryKey: ['agencies'] }),
      ])
    },
  })
}
